import { observer } from 'mobx-react';
import { FC, useEffect, useState } from 'react';
import { Prompt } from 'react-router';
import { useParams } from 'react-router-dom';
import { SCREENS } from 'src/constants/screens';
import { localeStore } from 'src/mobx/localesStore';
import { scrollToErrorElement } from 'src/utils/helpers';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import { getPromptProps } from 'src/view/components/CustomPrompt/helper';
import LoadingOverlay from 'src/view/components/LoadingOverlay/LoadingOverlay';
import TabList from 'src/view/components/Tabs/TabList';
import Tabs from 'src/view/components/Tabs/Tabs';
import HeaderBack from 'src/view/containers/Headers/components/HeaderBack/HeaderBack';
import Headers from 'src/view/containers/Headers/Headers';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import { BRANCH_EDIT_TAB } from 'src/view/screens/BranchesManagementScreen/screens/BranchEditScreen/constants/branchEdit';
import BranchDetails from 'src/view/screens/BranchesManagementScreen/screens/BranchEditScreen/containers/BranchDetails/BranchDetails';
import BranchDineIn from 'src/view/screens/BranchesManagementScreen/screens/BranchEditScreen/containers/BranchDineIn/BranchDineIn';
import branchEditStore from 'src/view/screens/BranchesManagementScreen/screens/BranchEditScreen/store/branchEditStore';
// eslint-disable-next-line
import BranchDeliverySettings from 'src/view/screens/BranchesManagementScreen/screens/BranchEditScreen/containers/BranchDeliverySettings/BranchDeliverySettings';
import { BUTTON_TITLE } from '~/constants/main';
import BranchesLogLinkLight from '~/view/components/Link/BranchesLogLinkLight/BranchesLogLinkLight';

const tabOptions = [
  { label: 'txt_details', value: BRANCH_EDIT_TAB.DETAILS },
  { label: 'branch_delivery_settings_tab', value: BRANCH_EDIT_TAB.DELIVERY_SETTINGS },
  { label: 'dine_in', value: BRANCH_EDIT_TAB.DINE_IN },
];

const tabs = [BRANCH_EDIT_TAB.DETAILS, BRANCH_EDIT_TAB.DELIVERY_SETTINGS, BRANCH_EDIT_TAB.DINE_IN];

const BranchEditScreen: FC = observer((): JSX.Element => {
  const [currentTabId, setCurrentTabId] = useState<BRANCH_EDIT_TAB>(BRANCH_EDIT_TAB.DETAILS);

  const { id: branchId } = useParams<{ id?: string }>();

  useEffect(() => {
    const beamerIcon = document.getElementById('beamerSelector');
    if (beamerIcon) {
      beamerIcon.style.bottom = '60px';
    }
    if (branchId) {
      branchEditStore.fetchBranch(branchId);
    }

    return () => {
      branchEditStore.clearEditableData();
      if (beamerIcon) {
        beamerIcon.style.bottom = '20px';
      }
    };
  }, []);

  const onSaveChanges = () => {
    branchEditStore.setValidation();

    if (branchEditStore.isValidationFailed) {
      scrollToErrorElement();
    } else if (branchId) {
      branchEditStore.updateBranch(branchId);
    } else {
      branchEditStore.createBranch();
    }
  };

  const onChangeTab = (tabId: number) => setCurrentTabId(tabId);

  return (
    <>
      <Headers>
        <HeaderBack title={branchEditStore.editableBranch.name[localeStore.language]} path={SCREENS.BRANCHES()} />
      </Headers>

      <ScrollableBlock showScrollbar scrollClassName="d-flex flex-column flex-1 pt-5">
        <Tabs value={currentTabId} options={tabOptions} errors={branchEditStore.tabsErrors} className="pb-6" onChange={onChangeTab} />

        <TabList tabs={tabs} value={currentTabId} className="flex-column">
          <BranchDetails />
          <BranchDeliverySettings />
          <BranchDineIn />
        </TabList>

        <Button title={localeStore.t(BUTTON_TITLE.SAVE)} onClick={onSaveChanges} loading={branchEditStore.loading} className="mt-8" />
      </ScrollableBlock>

      <BranchesLogLinkLight link={SCREENS.BRANCH_LOG(branchId ?? ':id')} />

      <LoadingOverlay visible={branchEditStore.loading} refreshing={branchEditStore.loading} />
      <Prompt when={branchEditStore.isDataWasEdited} message={getPromptProps()} />
    </>
  );
});

export default BranchEditScreen;
